import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, Fragment as _Fragment, renderList as _renderList } from "vue"

const _hoisted_1 = { class: "section lucky-numbers" }
const _hoisted_2 = {
  key: 0,
  class: "section-description"
}
const _hoisted_3 = {
  key: 1,
  class: "date-filter-section"
}
const _hoisted_4 = { class: "numbers-grid" }
const _hoisted_5 = {
  key: 2,
  class: "load-more-container"
}
const _hoisted_6 = ["disabled"]

import { ref, computed, nextTick } from "vue";

import LotteryCard from "./LotteryCard.vue";
import LotteryFilters from "./LotteryFilters.vue";
import NextDrawsFilter from "./NextDrawsFilter.vue";
import SectionHeader from "@/components/base/SectionHeader.vue";
import { iframeCommunication } from "@/utils/iframeCommunication";
import { useIntegrationMode } from '@/composables/useIntegrationMode';
import { useUpcomingDrawsStore } from '@/stores/upcomingDraws';


const __default__ = {
  name: 'LuckyNumbersSection',
};

export default /*@__PURE__*/_defineComponent({
  ...__default__,
  props: {
    title: {},
    description: {},
    games: {},
    showDatePicker: { type: Boolean },
    dateFilterOnly: { type: Boolean },
    showFilters: { type: Boolean, default: true },
    isFavorited: {},
    selectedDate: {},
    searchQuery: { default: '' },
    selectedCountry: { default: '' },
    titleAlign: {}
  },
  emits: ["bet", "favorite", "remove", "dateChange"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// Pagination state - 10 lines = 20 draws (2 per line on desktop)
const displayLimit = ref(20);
const liteLoadingMore = ref(false);
const { isLite } = useIntegrationMode();
const upcomingDrawsStore = useUpcomingDrawsStore();

// Filter states
const internalSearchQuery = ref('');
const internalSelectedCountry = ref('');
const internalSelectedDate = ref(props.selectedDate || '');
const sortBy = ref('default');

const activeSearchQuery = computed(() => props.searchQuery || internalSearchQuery.value);
const activeSelectedCountry = computed(() => props.selectedCountry || internalSelectedCountry.value);
const activeSelectedDate = computed(() => props.selectedDate || internalSelectedDate.value);

/** Countries that currently have draws (mobile country filter). */
const activeCountrySlugs = computed(() => {
  const slugs = new Set<string>();
  for (const game of props.games || []) {
    if (game?.country) slugs.add(String(game.country).toLowerCase());
  }
  return Array.from(slugs);
});

const isWithinTimeRange = (date: Date, range: string) => {
  const now = new Date();
  const drawDate = getValidDate(date);
  
  // If date is invalid, return false
  if (drawDate.getTime() === 0) return false;
  
  const hours = (drawDate.getTime() - now.getTime()) / (1000 * 60 * 60);
  
  switch (range) {
    case '1h': return hours <= 1;
    case '6h': return hours <= 6;
    case '12h': return hours <= 12;
    case '24h': return hours <= 24;
    default: return true;
  }
};

function sortGames(games: any[], sort: string) {
  switch (sort) {
    case 'time-asc':
      return [...games].sort((a, b) => {
        const dateA = getValidDate(a.cutoff || a.draw_date);
        const dateB = getValidDate(b.cutoff || b.draw_date);
        return dateA.getTime() - dateB.getTime();
      });
    case 'time-desc':
      return [...games].sort((a, b) => {
        const dateA = getValidDate(a.cutoff || a.draw_date);
        const dateB = getValidDate(b.cutoff || b.draw_date);
        return dateB.getTime() - dateA.getTime();
      });
    case 'country-asc':
      return [...games].sort((a, b) => (a.country || '').localeCompare(b.country || '', undefined, { sensitivity: 'base' }));
    case 'country-desc':
      return [...games].sort((a, b) => (b.country || '').localeCompare(a.country || '', undefined, { sensitivity: 'base' }));
    case 'name-asc':
      return [...games].sort((a, b) => (a.game_name || a.name || '').localeCompare(b.game_name || b.name || '', undefined, { sensitivity: 'base' }));
    case 'name-desc':
      return [...games].sort((a, b) => (b.game_name || b.name || '').localeCompare(a.game_name || a.name || '', undefined, { sensitivity: 'base' }));
    default:
      return games;
  }
}

// Helper function to safely parse dates
function getValidDate(dateString: any): Date {
  if (!dateString) return new Date(0); // Return epoch for null/undefined dates
  
  try {
    const dateStr = String(dateString);
    let date: Date;
    
    if (dateStr.includes('+')) {
      // UTC format like "2025-07-30 23:51:30+00:00"
      const datePart = dateStr.split('+')[0];
      date = new Date(datePart + 'Z');
    } else {
      // Fallback for other formats
      date = new Date(dateStr);
    }
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date format:', dateString);
      return new Date(0); // Return epoch for invalid dates
    }
    
    return date;
  } catch (error) {
    console.warn('Error parsing date:', dateString, error);
    return new Date(0); // Return epoch for error cases
  }
}

const filteredGames = computed(() => {
  let filtered = [...props.games];

  if (activeSearchQuery.value || activeSelectedCountry.value || (activeSelectedDate.value && activeSelectedDate.value !== '')) {
    filtered = filtered.filter(draw => {
      // Apply search filter
      const matchesSearch = !activeSearchQuery.value ||
        (draw.country && draw.country.toLowerCase().includes(activeSearchQuery.value.toLowerCase())) ||
        (draw.game_name && draw.game_name.toLowerCase().includes(activeSearchQuery.value.toLowerCase())) ||
        (draw.name && draw.name.toLowerCase().includes(activeSearchQuery.value.toLowerCase()));
      
      // Apply country filter
      const matchesCountry = !activeSelectedCountry.value || draw.country === activeSelectedCountry.value;
      
      // Apply date filter
      const matchesDate = !activeSelectedDate.value || activeSelectedDate.value === '' || (() => {
        if (!draw.draw_date) return false;
        
        try {
          // Parse the draw date properly (handle UTC format)
          const drawDateString = String(draw.draw_date);
          let drawDate: Date;
          
          if (drawDateString.includes('+')) {
            // UTC format like "2025-07-30 23:51:30+00:00"
            const datePart = drawDateString.split('+')[0];
            drawDate = new Date(datePart + 'Z');
          } else {
            // Fallback for other formats
            drawDate = new Date(drawDateString);
          }
          
          // Check if the date is valid
          if (isNaN(drawDate.getTime())) {
            return false;
          }
          
          // Get the date part in YYYY-MM-DD format
          const drawDateFormatted = drawDate.toISOString().split('T')[0];
          
          return drawDateFormatted === activeSelectedDate.value;
        } catch {
          return false;
        }
      })();
      
      return matchesSearch && matchesCountry && matchesDate;
    });
  }

  filtered = sortGames(filtered, sortBy.value);

  return filtered.slice(0, displayLimit.value);
});

const hasMoreGames = computed(() => {
  let filtered = sortGames(props.games, sortBy.value);

  if (activeSearchQuery.value || activeSelectedCountry.value || (activeSelectedDate.value && activeSelectedDate.value !== '')) {
    filtered = filtered.filter(draw => {
      // Apply search filter
      const matchesSearch = !activeSearchQuery.value ||
        (draw.country && draw.country.toLowerCase().includes(activeSearchQuery.value.toLowerCase())) ||
        (draw.game_name && draw.game_name.toLowerCase().includes(activeSearchQuery.value.toLowerCase())) ||
        (draw.name && draw.name.toLowerCase().includes(activeSearchQuery.value.toLowerCase()));
      
      // Apply country filter
      const matchesCountry = !activeSelectedCountry.value || draw.country === activeSelectedCountry.value;
      
      // Apply date filter
      const matchesDate = !activeSelectedDate.value || activeSelectedDate.value === '' || (() => {
        if (!draw.draw_date) return false;
        
        try {
          // Parse the draw date properly (handle UTC format)
          const drawDateString = String(draw.draw_date);
          let drawDate: Date;
          
          if (drawDateString.includes('+')) {
            // UTC format like "2025-07-30 23:51:30+00:00"
            const datePart = drawDateString.split('+')[0];
            drawDate = new Date(datePart + 'Z');
          } else {
            // Fallback for other formats
            drawDate = new Date(drawDateString);
          }
          
          // Check if the date is valid
          if (isNaN(drawDate.getTime())) {
            return false;
          }
          
          // Get the date part in YYYY-MM-DD format
          const drawDateFormatted = drawDate.toISOString().split('T')[0];
          
          return drawDateFormatted === activeSelectedDate.value;
        } catch {
          return false;
        }
      })();
      
      return matchesSearch && matchesCountry && matchesDate;
    });
  }

  return filtered.length > displayLimit.value;
});

const hasMoreLitePages = computed(() => {
  if (!isLite.value) return false;
  return upcomingDrawsStore.pagination.current_page < upcomingDrawsStore.pagination.total_pages;
});

const showLoadMore = computed(() => hasMoreGames.value || hasMoreLitePages.value);

const loadMore = async () => {
  if (isLite.value && hasMoreLitePages.value && !liteLoadingMore.value) {
    liteLoadingMore.value = true;
    try {
      await upcomingDrawsStore.fetchDraws(upcomingDrawsStore.pagination.current_page + 1);
      displayLimit.value += 10;
    } finally {
      liteLoadingMore.value = false;
    }
  } else {
    displayLimit.value += 10;
  }
  
  // Trigger immediate height update after DOM updates with a longer delay
  nextTick(() => {
    // Add extra delay to ensure all content is rendered
    setTimeout(() => {
      iframeCommunication.sendImmediateHeightUpdate();
    }, 100);
  });
};

const handleSearch = (query: string) => {
  internalSearchQuery.value = query;
};

const handleCountryFilter = (country: string) => {
  internalSelectedCountry.value = country;
};

const handleDateFilter = (date: string) => {
  internalSelectedDate.value = date;
  emit('dateChange', date); // Emit dateChange event
};

const handleSort = (sort: string) => {
  sortBy.value = sort;
};

const handleBet = (game: any) => {
  emit('bet', game);
};

const handleFavorite = (game: any) => {
  emit('favorite', game);
};

const handleRemove = (game: any) => {
  emit('remove', game);
};


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _renderSlot(_ctx.$slots, "header", {}, () => [
      _createVNode(SectionHeader, {
        title: _ctx.title,
        variant: "simple"
      }, null, 8, ["title"])
    ]),
    (_ctx.description)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("p", null, _toDisplayString(_ctx.description), 1)
        ]))
      : _createCommentVNode("", true),
    (_ctx.showFilters)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          (!_ctx.dateFilterOnly)
            ? (_openBlock(), _createBlock(LotteryFilters, {
                key: 0,
                "show-date-picker": _ctx.showDatePicker,
                "active-country-slugs": activeCountrySlugs.value,
                onSearch: handleSearch,
                onFilterCountry: handleCountryFilter,
                onFilterDate: handleDateFilter,
                onSort: handleSort
              }, null, 8, ["show-date-picker", "active-country-slugs"]))
            : (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createVNode(LotteryFilters, {
                  "active-country-slugs": activeCountrySlugs.value,
                  onSearch: handleSearch,
                  onFilterCountry: handleCountryFilter
                }, null, 8, ["active-country-slugs"]),
                _createVNode(NextDrawsFilter, {
                  modelValue: internalSelectedDate.value,
                  "onUpdate:modelValue": [
                    _cache[0] || (_cache[0] = ($event: any) => ((internalSelectedDate).value = $event)),
                    handleDateFilter
                  ]
                }, null, 8, ["modelValue"])
              ]))
        ], 64))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_4, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredGames.value, (game) => {
        return (_openBlock(), _createBlock(LotteryCard, {
          key: game.id,
          lottery: game,
          layout: "horizontal",
          "is-favorited": _ctx.isFavorited,
          onBet: ($event: any) => (handleBet(game)),
          onFavorite: ($event: any) => (handleFavorite(game)),
          onRemove: ($event: any) => (handleRemove(game))
        }, null, 8, ["lottery", "is-favorited", "onBet", "onFavorite", "onRemove"]))
      }), 128))
    ]),
    (showLoadMore.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createElementVNode("button", {
            class: "load-more-button",
            disabled: liteLoadingMore.value,
            onClick: loadMore
          }, _toDisplayString(_ctx.$t('forms.loadMoreNumberGames')), 9, _hoisted_6)
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})